import React, {
	createContext,
	useContext,
	ReactNode,
	useEffect,
	useMemo,
} from 'react'
import { CurrentUserObject } from '../lib/api/types/user.interfaces'
import { PlanNameType } from '../lib/api/types/billing.interfaces'
import { useLocation, useNavigate } from 'react-router-dom'

interface BillingContextType {
	features: number
	hasFeature: (feature: number) => boolean
	planName: PlanNameType
}

const BillingContext = createContext<BillingContextType | undefined>(undefined)

export const Features = {
	None: 0,
	Basic: 1 << 0, // Basic operations with files and folders create, delete, download, upload, simple search,
	Versions: 1 << 1, // ☑️
	Bin: 1 << 2, // ☑️
	OrgFolder: 1 << 3, // ☑️
	Classification: 1 << 4, // ☑️
	AdvancedClassification: 1 << 5, // ☑️
	Extraction: 1 << 6,
	AdvancedExtraction: 1 << 7,
	Qa: 1 << 8, // ☑️
	Nlp: 1 << 9, // ☑️
	Collaboration: 1 << 10, // ☑️ // Notes, Wiki, Todo, Contacts
	StandardSupport: 1 << 11,
	DedicatedSupport: 1 << 12,
	OnboardSupport: 1 << 13,
	// uncontrolled items:
	ManageUsers: 1 << 14, // ☑️
	Audit: 1 << 15, // ☑️
	ManageGroupsRolesPermissions: 1 << 16, // ☑️
}

export const hasFeature = (features: number, feature: number) => {
	return (features & feature) !== 0
}

const PlanStatus = {
	Active: 0,
	Expired: 1,
	Canceled: 3,
	Blocked: 3,
} as const

const normalizePlanStatus = (status?: number | string | null) => Number(status)

export const isExpiredPlanStatus = (status?: number | string | null) =>
	normalizePlanStatus(status) === PlanStatus.Expired

export const isBlockedPlanStatus = (status?: number | string | null) =>
	normalizePlanStatus(status) === PlanStatus.Blocked

export const isRestrictedPlanStatus = (status?: number | string | null) =>
	isExpiredPlanStatus(status) || isBlockedPlanStatus(status)

export const BillingProvider = ({
	children,
	currentUser,
}: {
	children: ReactNode
	currentUser: CurrentUserObject | null
}) => {
	const navigate = useNavigate()
	const location = useLocation()

	const isExpired = isExpiredPlanStatus(currentUser?.plan?.status)
	const isBlocked = isBlockedPlanStatus(currentUser?.plan?.status)
	const features = isExpired ? Features.None : currentUser?.plan?.features ?? 0
	const planName = isExpired ? 'Free' : currentUser?.plan?.name ?? 'Free'

	useEffect(() => {
		if (['/user-blocked', '/plan-block'].includes(location.pathname)) return

		// Only redirect if currentUser is loaded and plan is expired/blocked
		if (currentUser && (isExpired || isBlocked)) {
			navigate('/plan-block', { replace: true })
		}
	}, [currentUser, isExpired, isBlocked, navigate, location.pathname])

	const value = useMemo(
		() => ({
			features,
			hasFeature: (feature: number) => hasFeature(features, feature),
			planName,
		}),
		[features, planName]
	)

	return (
		<BillingContext.Provider value={value}>
			{children}
		</BillingContext.Provider>
	)
}

export const useBilling = () => {
	const ctx = useContext(BillingContext)
	if (!ctx) throw new Error('useBilling must be used within BillingProvider')
	return ctx
}
